import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Eye, EyeOff } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("brgt_token");
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

export default function AdminBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchBlogs = async () => {
    try {
      const { data } = await axios.get(`${API}/blogs`, authHeaders());
      setBlogs(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchBlogs(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/blogs/${id}`, authHeaders());
      toast.success("Deleted");
      fetchBlogs();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleStatus = async (blog) => {
    const newStatus = blog.status === "published" ? "draft" : "published";
    try {
      await axios.put(`${API}/blogs/${blog.id}`, { status: newStatus }, authHeaders());
      toast.success(`Post ${newStatus}`);
      fetchBlogs();
    } catch {
      toast.error("Failed to update");
    }
  };

  return (
    <div data-testid="admin-blogs">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy" data-testid="blogs-title">Blog Management</h1>
          <p className="text-slate-500 text-sm mt-1">Create and manage blog posts</p>
        </div>
        <Link
          to="/admin/blogs/new"
          data-testid="new-blog-btn"
          className="inline-flex items-center gap-2 bg-navy text-white hover:bg-navy-light rounded-full px-5 py-2.5 text-sm font-semibold transition-all"
        >
          <Plus size={16} /> New Post
        </Link>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="bg-white rounded-xl h-20 animate-pulse border border-slate-100" />)}
        </div>
      ) : blogs.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-12 text-center">
          <p className="text-slate-400 mb-4">No blog posts yet</p>
          <Link to="/admin/blogs/new" className="text-amber font-medium hover:underline">Create your first post</Link>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <table className="w-full" data-testid="blogs-table">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Title</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Category</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Date</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {blogs.map((blog) => (
                <tr key={blog.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`blog-row-${blog.id}`}>
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-navy truncate max-w-xs">{blog.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{blog.slug}</p>
                  </td>
                  <td className="px-6 py-4 hidden md:table-cell">
                    <Badge variant="outline" className="text-xs">{blog.category}</Badge>
                  </td>
                  <td className="px-6 py-4">
                    <Badge className={`text-xs ${blog.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {blog.status}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-xs text-slate-400 hidden md:table-cell">
                    {new Date(blog.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => toggleStatus(blog)}
                        data-testid={`toggle-status-${blog.id}`}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        title={blog.status === 'published' ? 'Unpublish' : 'Publish'}
                      >
                        {blog.status === 'published' ? <EyeOff size={14} className="text-slate-400" /> : <Eye size={14} className="text-emerald-500" />}
                      </button>
                      <button
                        onClick={() => navigate(`/admin/blogs/${blog.id}`)}
                        data-testid={`edit-blog-${blog.id}`}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <Edit size={14} className="text-slate-400" />
                      </button>
                      <button
                        onClick={() => handleDelete(blog.id)}
                        data-testid={`delete-blog-${blog.id}`}
                        className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        <Trash2 size={14} className="text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
